import { useCallback, useEffect, useMemo, useState } from "react";
import type { Api } from "./data/api.ts";
import { CloudApi } from "./data/cloudApi.ts";
import { DemoApi } from "./data/demoApi.ts";
import { ROLE_LABEL, type Organization, type Role } from "./data/types.ts";
import { AppContext, Icon, Logo, Toasts, navigate, permissionsOf, toast, useApp, useNow, useRoute } from "./ui/kit.tsx";
import { SupportBubble } from "./ui/Support.tsx";
import { AuthPages, CreateOrgPage, NewPasswordPage } from "./pages/Auth.tsx";
import { PlanningPage } from "./pages/Planning.tsx";
import { JobsPage } from "./pages/Jobs.tsx";
import { JobFormPage } from "./pages/JobForm.tsx";
import { LivePage } from "./pages/Live.tsx";
import { SettingsPage } from "./pages/Settings.tsx";
import { PostePage } from "./pages/Poste.tsx";

declare global {
  interface Window { ORDOPLI_CONFIG?: { supabaseUrl?: string; supabaseAnonKey?: string } }
}

const MODE_KEY = "ordopli-mode";
function getMode(): string | null { try { return localStorage.getItem(MODE_KEY); } catch { return null; } }
function setMode(v: string | null): void { try { v ? localStorage.setItem(MODE_KEY, v) : localStorage.removeItem(MODE_KEY); } catch { /* ignore */ } }

function makeApi(): Api | null {
  const cfg = window.ORDOPLI_CONFIG || {};
  if (location.hash.startsWith("#/demo")) setMode("demo");
  if (getMode() === "demo") return new DemoApi();
  if (cfg.supabaseUrl && /^https?:\/\//.test(cfg.supabaseUrl) && cfg.supabaseAnonKey) return new CloudApi(cfg.supabaseUrl, cfg.supabaseAnonKey);
  return null;
}

export function App() {
  const [api, setApi] = useState<Api | null>(() => makeApi());
  const route = useRoute();

  const enterDemo = useCallback(() => { setMode("demo"); setApi(new DemoApi()); navigate("planning"); }, []);
  const leaveDemo = useCallback(() => { setMode(null); setApi(makeApi()); navigate("connexion"); }, []);

  useEffect(() => { if (route[0] === "demo") enterDemo(); }, [route, enterDemo]);

  if (route[0] === "poste") {
    return api ? <><PostePage api={api} /><Toasts /></> : <NoConfig onDemo={enterDemo} />;
  }
  if (!api) return <NoConfig onDemo={enterDemo} />;
  return <><Authed api={api} leaveDemo={leaveDemo} enterDemo={enterDemo} /><Toasts /></>;
}

function Authed({ api, leaveDemo, enterDemo }: { api: Api; leaveDemo: () => void; enterDemo: () => void }) {
  const [session, setSession] = useState(api.session());
  const [org, setOrg] = useState<Organization | null | undefined>(undefined);
  const route = useRoute();
  // Retour d'un lien reçu par mail : lu une fois, avant le premier affichage.
  const [link] = useState(() => api.takeLinkNotice());
  const linkError = link?.kind === "error" ? link.message : "";
  useEffect(() => { if (link?.kind === "confirmed") toast(link.message); }, [link]);

  const refreshOrg = useCallback(async () => {
    if (!api.session()) { setOrg(null); return; }
    try { setOrg(await api.loadOrg()); } catch { setOrg(null); }
  }, [api]);

  useEffect(() => {
    setSession(api.session());
    refreshOrg();
    return api.subscribe(() => {
      const s = api.session();
      setSession((prev) => (prev?.user_id === s?.user_id ? prev : s));
    });
  }, [api, refreshOrg]);
  useEffect(() => { refreshOrg(); }, [session?.user_id, refreshOrg]);

  const ctx = useMemo(() => {
    if (!org) return null;
    const role: Role = org.my_role || "admin";
    return { api, org, refreshOrg, leaveDemo, role, can: permissionsOf(org, role) };
  }, [api, org, refreshOrg, leaveDemo]);

  if (!session) return <AuthPages api={api} route={route} onDemo={enterDemo} notice={linkError} />;
  if (route[0] === "nouveau-mot-de-passe") return <NewPasswordPage api={api} message={link?.kind === "recovery" ? link.message : undefined} />;
  if (org === undefined) return <div className="loading full">Chargement…</div>;
  if (org === null) return <CreateOrgPage api={api} onCreated={refreshOrg} />;
  return (
    <AppContext.Provider value={ctx!}>
      <Shell route={route} />
    </AppContext.Provider>
  );
}

const NAV = [
  { key: "planning", label: "Planning", short: "Planning", icon: "M4 5h16M4 10h10M4 15h13M4 20h7" },
  { key: "jobs", label: "Jobs", short: "Jobs", icon: "M6 3h9l4 4v14H6z M14 3v5h5 M9 13h7 M9 17h5" },
  { key: "atelier", label: "Atelier en direct", short: "Atelier", icon: "M3 12h4l3-7 4 14 3-7h4" },
  { key: "parametres", label: "Paramètres", short: "Réglages", icon: "M4 6h9 M17 6h3 M4 12h3 M11 12h9 M4 18h11 M19 18h1 M15 4v4 M9 10v4 M17 16v4" },
];
const POSTE_ICON = "M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M11 18h2";

function Shell({ route }: { route: string[] }) {
  const ctx = useApp();
  const page = route[0] || "planning";
  let body;
  if (page === "jobs" && route[1]) body = <JobFormPage id={route[1] === "nouveau" ? null : route[1]} copyOf={route[2] === "copie" ? route[1] : null} />;
  else if (page === "jobs") body = <JobsPage />;
  else if (page === "atelier") body = <LivePage />;
  else if (page === "parametres") body = <SettingsPage tab={route[1] || "imprimerie"} />;
  else body = <PlanningPage />;
  const isOn = (k: string) => page === k || (page === "" && k === "planning");
  return (
    <div className="shell">
      <aside className="side">
        <div className="brand"><Logo light /><div>Ordopli<small>{ctx.org.name}</small></div></div>
        <nav className="nav">
          {NAV.map((n) => (
            <a key={n.key} href={`#/${n.key}`} className={isOn(n.key) ? "on" : ""} aria-current={isOn(n.key) ? "page" : undefined} title={n.label}>
              <Icon d={n.icon} />
              <span>{n.label}</span>
            </a>
          ))}
        </nav>
        <a href="#/poste" target="_blank" rel="noopener" className="nav-poste" title="Ouvrir un poste tablette">
          <Icon d={POSTE_ICON} />
          <span>Ouvrir un poste tablette<small>L'écran des conducteurs</small></span>
        </a>
        <div className="side-foot">
          <div className="cmjn" aria-hidden="true"><i /><i /><i /><i /></div>
          {ctx.api.mode === "demo"
            ? <button className="link" onClick={ctx.leaveDemo}>Quitter la démo</button>
            : <><span>{ctx.api.session()?.email}</span><button className="link" onClick={() => ctx.api.signOut()}>Se déconnecter</button></>}
        </div>
      </aside>
      <main className="main">
        <TopBar />
        <div className="page-wrap" key={route.slice(0, 2).join("/")}>{body}</div>
      </main>
      <nav className="tabbar" aria-label="Navigation">
        {NAV.map((n) => (
          <a key={n.key} href={`#/${n.key}`} className={isOn(n.key) ? "on" : ""} aria-current={isOn(n.key) ? "page" : undefined}>
            <Icon d={n.icon} size={22} /><span>{n.short}</span>
          </a>
        ))}
      </nav>
      <SupportBubble />
    </div>
  );
}

/** Une seule barre : date, semaine, mode démo, heure. */
function TopBar() {
  const { api, role, refreshOrg, leaveDemo, org } = useApp();
  const now = useNow(15_000);
  const date = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const demo = api.mode === "demo" ? (api as DemoApi) : null;
  return (
    <header className="topbar">
      <span className="tb-brand" aria-hidden="true"><Logo size={24} /></span>
      <span className="tb-date">{date}</span>
      <span className="tb-date-short">{now.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</span>
      <span className="tb-week" title="Numéro de semaine">Semaine {isoWeek(now)}</span>
      <span className="tb-org">{org.name}</span>
      <span className="spacer" />
      {demo ? (
        <div className="tb-demo">
          <span className="tb-demo-tag" title="Atelier fictif, gardé dans ce navigateur">Démo</span>
          <label className="tb-demo-role"><span>Voir en tant que</span>
            <select value={role} onChange={async (e) => { demo.setRole(e.target.value as Role); await refreshOrg(); }} aria-label="Voir en tant que">
              {(Object.keys(ROLE_LABEL) as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </label>
          <button className="tb-link" onClick={() => { demo.reset(); refreshOrg(); toast("Démonstration remise à zéro"); }}>Remettre à zéro</button>
          <button className="tb-link tb-exit" onClick={leaveDemo}>Quitter</button>
        </div>
      ) : (
        <>
          <span className="tb-role">{ROLE_LABEL[role]}</span>
          <button className="tb-link tb-exit" onClick={() => api.signOut()}>Déconnexion</button>
        </>
      )}
      <time className="tb-clock" dateTime={now.toISOString()}>{now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</time>
    </header>
  );
}

export function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - y0.getTime()) / 86400000 + 1) / 7);
}

function NoConfig({ onDemo }: { onDemo: () => void }) {
  return (
    <div className="auth">
      <div className="auth-card">
        <Logo size={40} />
        <h1>Ordopli</h1>
        <p className="muted">L'application n'est pas encore reliée à sa base de données.</p>
        <button className="btn btn-primary btn-block" onClick={onDemo}>Essayer la démonstration</button>
      </div>
    </div>
  );
}
